import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { ArrowLeft } from "lucide-react";
import { Button, Card, Input, Spinner } from "@nutrayan/ui";
import {
  getProductBySlug,
  updateProduct,
  editProductSchema,
  type EditProductValues,
} from "@/lib/catalog-api";
import { StatusBadge } from "@/components/StatusBadge";
import { ProductFields } from "./ProductFields";

export default function EditProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const product = useQuery({
    queryKey: ["admin", "catalog-product", slug],
    queryFn: () => getProductBySlug(slug ?? ""),
    enabled: !!slug,
  });

  const form = useForm<EditProductValues>({
    resolver: zodResolver(editProductSchema),
    defaultValues: {
      name: "",
      shortDescription: "",
      longDescription: "",
      classicalReference: "",
      subscribable: true,
      displayOrder: 0,
    },
  });
  const { reset } = form;

  // Prefill once the product loads.
  useEffect(() => {
    const p = product.data;
    if (!p) return;
    reset({
      name: p.name ?? "",
      shortDescription: p.shortDescription ?? "",
      longDescription: p.longDescription ?? "",
      classicalReference: p.classicalReference ?? "",
      subscribable: p.subscribable ?? true,
      displayOrder: p.displayOrder ?? 0,
    });
  }, [product.data, reset]);

  const save = useMutation({
    mutationFn: (values: EditProductValues) => updateProduct(product.data!.id, values),
    onSuccess: (updated) => {
      toast.success(`Saved: ${updated.name ?? "product"}`);
      qc.invalidateQueries({ queryKey: ["admin", "products"] });
      qc.invalidateQueries({ queryKey: ["admin", "catalog-product", slug] });
      navigate("/catalog");
    },
    onError: (e: Error) => toast.error(e.message || "Couldn't save the product"),
  });

  if (product.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }
  if (product.isError || !product.data) {
    return (
      <Card>
        <p className="text-body text-terracotta">Couldn&apos;t load this product.</p>
        <Link to="/catalog" className="text-forest mt-3 inline-block underline">
          Back to Catalog
        </Link>
      </Card>
    );
  }

  const p = product.data;

  return (
    <div className="mx-auto max-w-3xl space-y-6 py-2">
      <Link to="/catalog" className="text-caption text-forest inline-flex items-center gap-1">
        <ArrowLeft size={13} aria-hidden /> Catalog
      </Link>

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-display-md text-charcoal">Edit product</h1>
            <p className="text-body-sm text-ink mt-1 font-mono">{p.slug}</p>
          </div>
          <StatusBadge status={p.status === "PUBLISHED" ? "ACTIVE" : p.status} />
        </div>

        {/* Read-only, immutable-after-create fields. */}
        <div className="bg-sand text-caption text-ink mt-4 grid gap-2 rounded-md p-3 sm:grid-cols-3">
          <span>
            <span className="text-muted">Line:</span> {p.line ?? "—"}
          </span>
          <span>
            <span className="text-muted">Slug:</span> {p.slug ?? "—"}
          </span>
          <span>
            <span className="text-muted">Trial pack:</span> {p.trialPack ? "Yes" : "No"}
          </span>
        </div>

        <form
          className="mt-5 grid gap-3 sm:grid-cols-2"
          onSubmit={form.handleSubmit((values) => save.mutate(values))}
        >
          <ProductFields
            register={form.register}
            errors={form.formState.errors}
            disabled={save.isPending}
          />

          <div>
            <label className="text-body-sm text-ink" htmlFor="displayOrder">
              Display order
            </label>
            <Input
              id="displayOrder"
              type="number"
              min={0}
              disabled={save.isPending}
              {...form.register("displayOrder")}
            />
          </div>

          <div className="flex items-center gap-2 sm:col-span-2">
            <Button type="submit" size="lg" disabled={save.isPending || !form.formState.isDirty}>
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
            <Button type="button" size="lg" variant="ghost" onClick={() => navigate("/catalog")}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
